import React from 'react';

/**
 * Gemeinsame UI-Primitive für ein einheitliches, professionelles Erscheinungsbild.
 * Alle Seiten verwenden diese Bausteine (Buttons, Cards, Modal, Formularfelder,
 * Badges, Tabellen-Stile), damit Abstände, Farben und Zustände konsistent sind.
 */

/* ------------------------------------------------------------------ Icons -- */
type IconProps = { className?: string };
const svg = (path: React.ReactNode, viewBox = '0 0 24 24'): React.FC<IconProps> =>
  ({ className = 'h-5 w-5' }) => (
    <svg className={className} viewBox={viewBox} fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
  );

export const Icon = {
  Plus: svg(<path d="M12 5v14M5 12h14" />),
  Search: svg(<><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></>),
  Download: svg(<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />),
  Edit: svg(<path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />),
  Trash: svg(<path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m2 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" />),
  ChevronLeft: svg(<path d="M15 18l-6-6 6-6" />),
  ChevronRight: svg(<path d="M9 18l6-6-6-6" />),
  ArrowRight: svg(<path d="M5 12h14M13 6l6 6-6 6" />),
  ArrowLeft: svg(<path d="M19 12H5M11 18l-6-6 6-6" />),
  MapPin: svg(<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0116 0z" /><circle cx="12" cy="10" r="3" /></>),
  Paw: svg(<><circle cx="6" cy="11" r="1.6" /><circle cx="10" cy="7" r="1.6" /><circle cx="14" cy="7" r="1.6" /><circle cx="18" cy="11" r="1.6" /><path d="M8 17a4 4 0 018 0 2.5 2.5 0 01-2.5 2.5c-.8 0-1.2-.4-1.5-.4s-.7.4-1.5.4A2.5 2.5 0 018 17z" /></>),
  Close: svg(<path d="M18 6L6 18M6 6l12 12" />),
  Calendar: svg(<><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>),
  Clock: svg(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>),
  Chat: svg(<path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />),
  Doc: svg(<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6" /></>),
  Upload: svg(<path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />),
  Eye: svg(<><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" /></>),
  // Navigation und Bereiche – derselbe feine Strich wie oben, statt Emojis.
  Dashboard: svg(<><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>),
  Map: svg(<path d="M9 4L3 6v14l6-2 6 2 6-2V4l-6 2-6-2zM9 4v14M15 6v14" />),
  Grabstein: svg(<path d="M6 21V10a6 6 0 0112 0v11M3.5 21h17M10 11h4M10 14.5h4" />),
  Kunden: svg(<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0M16 4.6a3.5 3.5 0 010 6.8M18 14.2a6.5 6.5 0 013.5 5.8" /></>),
  Rechnung: svg(<path d="M6 2.5h12v19l-3-2-3 2-3-2-3 2v-19zM9 7.5h6M9 11h6M9 14.5h3.5" />),
  Artikel: svg(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0L3 13V3h10l7.6 7.6a2 2 0 010 2.8z" /><circle cx="7.5" cy="7.5" r="1.3" /></>),
  Baum: svg(<path d="M12 2.5L6.5 9.5h3L5 15.5h14l-4.5-6h3L12 2.5zM12 15.5v6M9.5 21.5h5" />),
  Gebaeude: svg(<path d="M4.5 21V4a1 1 0 011-1h13a1 1 0 011 1v17M3 21h18M8.5 7h2M13.5 7h2M8.5 11h2M13.5 11h2M8.5 15h2M13.5 15h2M10.5 21v-2.5h3V21" />),
  Regler: svg(<><path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" /><circle cx="15" cy="6" r="2" /><circle cx="9" cy="12" r="2" /><circle cx="17" cy="18" r="2" /></>),
  Verlauf: svg(<path d="M3.5 12a8.5 8.5 0 102.5-6L3.5 8.5M3.5 3.5v5h5M12 7.5V12l3 2" />),
  Schild: svg(<><path d="M12 21.5s7.5-3.6 7.5-9.5V5.2L12 2.5 4.5 5.2V12c0 5.9 7.5 9.5 7.5 9.5z" /><circle cx="12" cy="10" r="2.3" /><path d="M8.3 16a4.2 4.2 0 017.4 0" /></>),
  Schluessel: svg(<><circle cx="7.5" cy="15.5" r="4" /><path d="M10.4 12.6L20 3M16.5 6.5l2.5 2.5M14 9l2 2" /></>),
  Abmelden: svg(<path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />),
  Menue: svg(<path d="M4 6.5h16M4 12h16M4 17.5h16" />),
  ChevronUp: svg(<path d="M18 15l-6-6-6 6" />),
  ChevronDown: svg(<path d="M6 9l6 6 6-6" />),
  Wechseln: svg(<path d="M20.5 12a8.5 8.5 0 11-2.6-6.1L20.5 8.5M20.5 3.5v5h-5" />),
  // Lageplan-Werkzeuge
  Standort: svg(<><circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2.5" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>),
  Nummern: svg(<path d="M4.5 9h16M3.5 15h16M10.5 3.5L8.5 20.5M15.5 3.5l-2 17" />),
  Vollbild: svg(<path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M16 21h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />),
};

/**
 * Ein Grab als kleiner Grabstein (oben gewölbt) in seiner Statusfarbe – dieselben Farben wie im
 * Lageplan (STATUS_FILL). Dashboard und Gräberliste zeigen den Status so, statt mit bunten Pillen.
 */
export const GrabStein: React.FC<{ farbe: string; klein?: boolean }> = ({ farbe, klein }) => (
  <span
    aria-hidden
    className={`inline-block shrink-0 rounded-b-[2px] rounded-t-full ${klein ? 'h-3 w-2.5' : 'h-5 w-4'}`}
    style={{ backgroundColor: farbe }}
  />
);

/* ---------------------------------------------------------------- Spinner -- */
export const Spinner: React.FC<IconProps> = ({ className = 'h-5 w-5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

/** Ganzseitiger Ladezustand (vertikal/horizontal zentriert). */
export const LoadingScreen: React.FC = () => (
  <div className="flex items-center justify-center p-16 text-brand-600">
    <Spinner className="h-10 w-10" />
  </div>
);

/* ----------------------------------------------------------------- Button -- */
type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
  icon?: React.ReactNode;
}

const BTN_VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-brand-600 text-white shadow-sm hover:bg-brand-700 focus:ring-brand-500',
  secondary: 'bg-white text-gray-700 border border-gray-300 shadow-sm hover:bg-gray-50 focus:ring-brand-500',
  danger: 'bg-white text-red-600 border border-red-200 shadow-sm hover:bg-red-50 focus:ring-red-400',
  ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-300',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary', size = 'md', loading, icon, className = '', children, disabled, ...rest
}) => (
  <button
    disabled={disabled || loading}
    className={`inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
      size === 'sm' ? 'px-3 py-1.5 text-sm' : 'px-4 py-2.5 text-sm'
    } ${BTN_VARIANT[variant]} ${className}`}
    {...rest}
  >
    {loading ? <Spinner className="h-4 w-4" /> : icon}
    {children}
  </button>
);

/* ------------------------------------------------------------ Row actions -- */
/** Dezenter Text-Button für Tabellen-Aktionen (Bearbeiten / Details). */
export const RowAction: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }> =
  ({ danger, className = '', ...rest }) => (
    <button
      className={`rounded-md px-2 py-1 text-sm font-medium transition-colors ${
        danger ? 'text-red-600 hover:bg-red-50' : 'text-brand-700 hover:bg-brand-50'
      } ${className}`}
      {...rest}
    />
  );

/* ------------------------------------------------------------------- Card -- */
// forwardRef, damit eine Karte nach dem Speichern ins Bild geholt werden kann (utils/ankunft.ts).
export const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className = '', ...rest }, ref) => (
    <div ref={ref} className={`rounded-2xl border border-gray-200 bg-white shadow-sm ${className}`} {...rest} />
  ),
);
Card.displayName = 'Card';

/* ------------------------------------------------------------------ Badge -- */
export const Badge: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`}>
    {children}
  </span>
);

/* ------------------------------------------------------------- PageHeader -- */
export const PageHeader: React.FC<{
  title: string;
  subtitle?: string;
  chip?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ title, subtitle, chip, actions }) => (
  <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-gray-200 pb-5">
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
        {chip}
      </div>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/** Kontext-Chip (z. B. aktiver Friedhof) für den Seitenkopf. */
export const ContextChip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-sm font-medium text-brand-800">
    <span className="h-2 w-2 rounded-full bg-brand-500" />
    {children}
  </span>
);

/* ------------------------------------------------------------------ Modal -- */
export const Modal: React.FC<{
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}> = ({ title, onClose, children, maxWidth = 'max-w-lg' }) => (
  <div className="ui-fade fixed inset-0 z-[1300] flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm" onMouseDown={onClose}>
    <div
      className={`ui-pop flex max-h-[90vh] w-full ${maxWidth} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5`}
      onMouseDown={e => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600" aria-label="Schließen">
          <Icon.Close className="h-5 w-5" />
        </button>
      </div>
      <div className="overflow-y-auto px-6 py-5">{children}</div>
    </div>
  </div>
);

/** Aktionsleiste am Fuß eines Formulars im Modal. */
export const FormActions: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-6 flex justify-end gap-2 border-t border-gray-100 pt-4">{children}</div>
);

/* ------------------------------------------------------------ Form fields -- */
/** Auf Touch-Geräten (iPad) mindestens 44 px hoch – Apples Mindestgröße für Tippflächen. */
export const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm transition focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-gray-100 disabled:text-gray-500 [@media(pointer:coarse)]:min-h-[44px]';

export const Field: React.FC<{ label: React.ReactNode; required?: boolean; hint?: string; className?: string; children: React.ReactNode }> =
  ({ label, required, hint, className = '', children }) => (
    <div className={className}>
      <label className="mb-1 block text-sm font-medium text-gray-700">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );

export const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className = '', ...rest }) => (
  <input className={`${inputClass} ${className}`} {...rest} />
);

/* -------------------------------------------------------------- DateInput -- */
/**
 * Datumsfeld, das schnelles Tippen erlaubt (deutsches Format TT.MM.JJJJ) und
 * trotzdem einen Kalender-Picker anbietet. Statt `<input type="date">` (öffnet
 * auf dem iPad immer den Picker und lässt keine Tastatureingabe zu) wird ein
 * Textfeld mit numerischer Tastatur genutzt. `value`/`onChange` arbeiten weiterhin
 * mit ISO-Strings (JJJJ-MM-TT), damit der Austausch in den Formularen 1:1 passt.
 */
const isoToDe = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};
const deToIso = (de: string): string => {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec((de || '').trim());
  if (!m) return '';
  const d = m[1].padStart(2, '0'), mo = m[2].padStart(2, '0'), y = m[3];
  // Parsed as local time (no offset), so validate with the local getters — using getUTC* here would
  // shift to the previous day in any UTC+ timezone (e.g. Europe/Berlin) and reject every date.
  const dt = new Date(`${y}-${mo}-${d}T00:00:00`);
  if (isNaN(dt.getTime()) || dt.getMonth() + 1 !== Number(mo) || dt.getDate() !== Number(d)) return '';
  return `${y}-${mo}-${d}`;
};
const formatDe = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return digits;
};

export const DateInput: React.FC<{
  value: string;
  onChange: (iso: string) => void;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
}> = ({ value, onChange, required, disabled, id, name, className = '' }) => {
  const [text, setText] = React.useState(() => isoToDe(value));
  const nativeRef = React.useRef<HTMLInputElement>(null);

  // Externe Änderungen des ISO-Werts übernehmen, aber unfertige Eingaben nicht überschreiben.
  React.useEffect(() => {
    setText((prev) => (deToIso(prev) === value ? prev : isoToDe(value)));
  }, [value]);

  const handleText = (raw: string) => {
    const formatted = formatDe(raw);
    setText(formatted);
    onChange(deToIso(formatted));
  };

  const openPicker = () => {
    const n = nativeRef.current;
    if (!n) return;
    if (typeof n.showPicker === 'function') n.showPicker();
    else n.click();
  };

  return (
    <div className={`relative ${className}`}>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="TT.MM.JJJJ"
        value={text}
        required={required}
        disabled={disabled}
        id={id}
        name={name}
        onChange={(e) => handleText(e.target.value)}
        className={`${inputClass} pr-10`}
      />
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pointer-events-none absolute right-2 bottom-0 h-0 w-0 opacity-0"
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        onClick={openPicker}
        aria-label="Kalender öffnen"
        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-gray-400 transition hover:text-brand-600 disabled:opacity-50"
      >
        <Icon.Calendar className="h-5 w-5" />
      </button>
    </div>
  );
};
export const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className = '', ...rest }) => (
  <textarea className={`${inputClass} ${className}`} {...rest} />
);
export const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className = '', children, ...rest }) => (
  <select className={`${inputClass} cursor-pointer ${className}`} {...rest}>{children}</select>
);

/* ---------------------------------------------------------------- Filter -- */
/**
 * Ein Filterfeld, das vom Standard abweicht, färbt sich grün – so sieht man auf einen Blick, *welcher*
 * Filter die Liste einschränkt. Auf Select/Input/Suchfeld anwenden: `${aktiv ? filterAktiv : ''}`.
 */
export const filterAktiv = '!border-brand-500 !bg-brand-50 !text-brand-900 font-medium';

/**
 * Rechts in der Filterleiste: ohne Filter „24 Rechnungen“, mit Filter „1 von 24“ (die Zahl sagt,
 * dass etwas ausgeblendet ist) plus deutlicher Knopf zum Zurücksetzen.
 */
export const FilterStand: React.FC<{
  treffer: number; gesamt: number; einheit: string; aktiv: boolean; onZuruecksetzen: () => void;
}> = ({ treffer, gesamt, einheit, aktiv, onZuruecksetzen }) => (
  aktiv ? (
    <span className="ml-auto flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-sm text-gray-500">
      <span className="whitespace-nowrap tabular-nums">
        <span className="font-semibold text-gray-900">{treffer}</span> von {gesamt}
      </span>
      <button
        type="button"
        onClick={onZuruecksetzen}
        className="inline-flex min-h-[36px] items-center gap-1.5 whitespace-nowrap rounded-full bg-brand-50 px-3 text-sm font-medium text-brand-800 ring-1 ring-brand-200 transition-colors hover:bg-brand-100"
      >
        <Icon.Close className="h-3.5 w-3.5" />Filter zurücksetzen
      </button>
    </span>
  ) : (
    <span className="ml-auto whitespace-nowrap text-sm tabular-nums text-gray-500">{gesamt} {einheit}</span>
  )
);

/* ------------------------------------------------------------------ Table -- */
// overflow-clip statt -hidden: rundet die Ecken ab, ohne eine Scrollfläche zu bilden – sonst
// blieben die Spaltenköpfe beim Scrollen nicht oben stehen.
export const tableCard = 'overflow-clip rounded-2xl border border-gray-200 bg-white shadow-sm';
// Hülle um die Tabelle: auf dem Handy seitlich scrollbar, ab Tablet-Breite ohne eigene Scrollfläche.
export const tableScroll = 'overflow-x-auto md:overflow-x-visible';
// Spaltenköpfe normal geschrieben statt gesperrter Großbuchstaben – ruhiger und besser lesbar.
// Mitlaufend: beim Scrollen langer Listen bleiben die Spaltenköpfe oben stehen.
export const th = 'sticky top-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-semibold text-gray-500 shadow-[inset_0_-1px_0_#e5e7eb]';
export const td = 'px-6 py-3.5 text-sm';
export const tableRow = 'transition-colors hover:bg-gray-50/70';

/** Leerer-Zustand-Block für Listen/Tabellen. */
/**
 * Leerer Zustand. Eine leere Liste ist eine Einladung zu handeln: wo es einen nächsten Schritt gibt,
 * steht er als `action` darunter (z. B. „Kunde anlegen“, bei leerem Filter „Filter zurücksetzen“).
 */
export const EmptyState: React.FC<{ icon?: React.ReactNode; title: string; hint?: string; action?: React.ReactNode }> =
  ({ icon, title, hint, action }) => (
    <div className="px-6 py-14 text-center">
      {icon && (
        <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-gray-100 text-gray-400">{icon}</div>
      )}
      <p className="font-medium text-gray-700">{title}</p>
      {hint && <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">{hint}</p>}
      {action && <div className="mt-5 flex justify-center gap-2">{action}</div>}
    </div>
  );
